import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import usePath from 'helpers/hooks/usePath';
import { toUIColor } from 'helpers/mappers/toUIColor';
import { toUIProduct } from 'helpers/mappers/toUIProduct';
import { toUISize } from 'helpers/mappers/toUIsize';
import dynamic from 'next/dynamic';
import { FC, useEffect, useState } from 'react';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import { ProjectConfig } from 'types/project-config';
import ProductDetails, { ProductDetailsProps } from '..';
import ProductDetailsCertificationCards from '../certificaton-cards';
import ProductDetailsCoursesNonPurchasable, {
  ProductDetailsCoursesNonPurchasableProps,
} from '../courses-non-purchasable';
import ProductDetailsCoursesSellable from '../courses-sellable';
import ProductDetailsPadiGear from '../gear';
// import ProductDetailsSubscription from '../subscription';

const DynamicProductDetailsSubscription = dynamic(() => import('../subscription'));

type ProductDetailsAdapterProps = {
  product: Product;
  inModalVersion?: ProductDetailsProps['inModalVersion'] | ProductDetailsCoursesNonPurchasableProps['inModalVersion'];
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
  projectConfig: ProjectConfig;
  isLazyLoad?: boolean;
  isTurnOnGallery?: boolean;
};

const ProductDetailsAdapter: FC<ProductDetailsAdapterProps> = ({
  product,
  inModalVersion,
  setIsOpen,
  onAddToCart,
  projectConfig,
  isLazyLoad,
  isTurnOnGallery,
}) => {
  const { path } = usePath();
  const [variant, setVariant] = useState<Variant>();
  const [mappedProduct, setMappedProduct] = useState<UIProduct>();
  const categories = product?.categories;
  const productType = product?.productType;
  const ProductDetailsSubscription = DynamicProductDetailsSubscription;

  useEffect(() => {
    if (product && variant) {
      const colors = toUIColor(product);
      const sizes = toUISize(product);
      const productToUse = toUIProduct(product, variant, colors, sizes);
      setMappedProduct({ ...productToUse, images: [productToUse.images[0]] });
    }
  }, [product, variant]);

  useEffect(() => {
    if (!product) return;

    if (inModalVersion) {
      setVariant(product?.variants[0]);
    } else {
      const currentVariantPath = path.split('/');
      const currentVariantSKU = currentVariantPath[3]?.split('?')[0];
      const currentVariantIndex = product?.variants.findIndex(({ sku }) => sku == currentVariantSKU);
      setVariant(product.variants[currentVariantIndex] ?? product.variants?.[0]);
    }
  }, [inModalVersion, product, path]);

  const handleChangeVariant = (sku: string) => {
    const variantsToUse = product.variants.find((variant) => variant.sku === sku);
    setVariant(variantsToUse);
  };

  if (!product || !variant)
    // Fill above the fold to reduce LCP druing page load.
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 md:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="h-400 animate-pulse rounded-lg bg-gray-200" />
          <div className="space-y-4">
            <div className="h-8 animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      </div>
    );

  switch (productType) {
    case 'Course':
      if (categories) {
        for (let i = 0; i < categories?.length; i++) {
          // Non-purchasable courses.
          if (categories[i].slug == 'not-purchasable-online') {
            return (
              <ProductDetailsCoursesNonPurchasable
                product={mappedProduct as UIProduct}
                variant={variant}
                url={product._url}
                inModalVersion={inModalVersion}
                onChangeVariant={handleChangeVariant}
                setIsOpen={setIsOpen}
                onAddToCart={onAddToCart}
                projectConfig={projectConfig}
                isLazyLoad={isLazyLoad}
                isTurnOnGallery={isTurnOnGallery}
              />
            );
          }
        }
      }
      return (
        <ProductDetailsCoursesSellable
          product={mappedProduct as UIProduct}
          variant={variant}
          url={product._url}
          inModalVersion={inModalVersion}
          onChangeVariant={handleChangeVariant}
          setIsOpen={setIsOpen}
          onAddToCart={onAddToCart}
          isLazyLoad={isLazyLoad}
          isTurnOnGallery={isTurnOnGallery}
        />
      );
    case 'certification-card':
      return (
        <ProductDetailsCertificationCards
          product={mappedProduct as UIProduct}
          variant={variant}
          url={product._url}
          onChangeVariant={handleChangeVariant}
          setIsOpen={setIsOpen}
          onAddToCart={onAddToCart}
          projectConfig={projectConfig}
        />
      );
    case 'subscription':
      return (
        <ProductDetailsSubscription
          product={mappedProduct as UIProduct}
          variant={variant}
          url={product._url}
          inModalVersion={inModalVersion}
          onChangeVariant={handleChangeVariant}
          setIsOpen={setIsOpen}
          onAddToCart={onAddToCart}
        />
      );
    case 'padi-gear':
      return (
        <ProductDetailsPadiGear
          product={mappedProduct as UIProduct}
          variant={variant}
          url={product._url}
          inModalVersion={inModalVersion}
          onChangeVariant={handleChangeVariant}
          setIsOpen={setIsOpen}
          onAddToCart={onAddToCart}
        />
      );
    default:
      return (
        <ProductDetails
          product={mappedProduct as UIProduct}
          variant={variant}
          url={product._url}
          inModalVersion={inModalVersion}
          onChangeVariant={handleChangeVariant}
          setIsOpen={setIsOpen}
          onAddToCart={onAddToCart}
        />
      );
  }
};

export default ProductDetailsAdapter;
